import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { leadService, type PendingDiscount } from '../modules/leads/services/leads'
import { authService } from '../modules/identity/services/auth'
import { UserRole } from '../types'

interface PendingDiscountsState {
  pending: PendingDiscount[]
  count: number
  refresh: () => void
}

const PendingDiscountsContext = createContext<PendingDiscountsState>({
  pending: [],
  count: 0,
  refresh: () => {},
})

const APPROVER_ROLES: UserRole[] = [
  UserRole.TEAM_LEADER,
  UserRole.INSURANCE_HEAD,
  UserRole.DEALER_PRINCIPAL,
]

export function PendingDiscountsProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingDiscount[]>([])

  const refresh = useCallback(() => {
    const user = authService.getCurrentUser()
    if (!user || !APPROVER_ROLES.includes(user.role as UserRole)) return
    leadService.getPendingDiscounts()
      .then(setPending)
      .catch(() => {})
  }, [])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, 10_000)
    const onFocus = () => refresh()
    window.addEventListener('focus', onFocus)
    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', onFocus)
    }
  }, [refresh])

  return (
    <PendingDiscountsContext.Provider value={{ pending, count: pending.length, refresh }}>
      {children}
    </PendingDiscountsContext.Provider>
  )
}

export function usePendingDiscounts() {
  return useContext(PendingDiscountsContext)
}

export { APPROVER_ROLES }
